import { buildHTMLWithTags, getTwitterHtmlFromText } from 'utils/composer'
import { sanitizeComposerHtml } from 'utils/sanitize'
import TagPopup from 'components/TagPopup'
import { PostDestinationType, SocialProfileTag } from 'shared/types'
import { memo, useState, useRef, useEffect, Fragment } from 'react'

import styles from './PostText.pcss'
import { BRAND_TWITTER } from 'shared'

export interface PostTextProps {
  text: string
  network: PostDestinationType
  tags?: SocialProfileTag[]
  linkedinFormattedText?: string
  className?: string
}

export const PostText = memo((props: PostTextProps) => {
  const [tagPopupAnchor, setTagPopupAnchor] = useState<HTMLElement | null>(null)
  const textElementRef = useRef<HTMLDivElement>(null)
  const withTags = Boolean(props.tags)
  const [isParsed, setIsParsed] = useState(!withTags)

  const onTagMouseEnter = (e: Event) => {
    const element = e.target as HTMLElement
    setTagPopupAnchor(element)
  }

  const onTagMouseLeave = () => {
    setTagPopupAnchor(null)
  }

  useEffect(() => {
    const textElement = textElementRef.current
    // NOTE: `props.text` is the post's plain-text status and every branch below writes it with
    // `innerHTML`, so the browser reparses it as markup. It reaches us from the API / feeds, so
    // it is sanitized here regardless of which branch builds the html.
    if (props.network === BRAND_TWITTER) {
      if (textElement) {
        textElement.innerHTML = sanitizeComposerHtml(getTwitterHtmlFromText(props.text))
      }
      return
    }
    if (props.tags && Array.isArray(props.tags) && textElement) {
      // NOTE: buildHTMLWithTags sanitizes its own return value
      const htmlString = buildHTMLWithTags(props.text, props.tags, props.network, styles.tag, props.linkedinFormattedText)
      textElement.innerHTML = htmlString
      textElement.querySelectorAll('[data-tag-id]').forEach(element => {
        element.addEventListener('mouseenter', onTagMouseEnter)
        element.addEventListener('mouseleave', onTagMouseLeave)
      })
      setIsParsed(true)
      return
    }

    if (textElement) {
      textElement.innerHTML = sanitizeComposerHtml(props.text)
    }
  }, [props.network, props.tags, props.text, props.linkedinFormattedText])

  return (
    <Fragment>
      <div ref={textElementRef} className={`${isParsed ? '' : styles.hidden} ${props.className || ''}`}>
      </div>
      <TagPopup anchor={tagPopupAnchor} />
    </Fragment>
  )
})

export default PostText
